/*
 *
 * UpcomingAssignmentsCard
 *
 */

import moment from 'moment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ShiftLeaderIcon from '@mui/icons-material/AccountCircle';
import PublisherIcon from '@mui/icons-material/PermIdentity';
import CanceledIcon from '@mui/icons-material/Cancel';
import { FormattedMessage, useIntl } from 'react-intl';
import ScrollablePaper from '~/components/ScrollablePaper';
import TagList from '~/components/TagList';
import TagVehicle from '~/components/Tag/Vehicle';
import { AssignmentSummaryResource } from '~/generated';
import ListItemButton from '@mui/material/ListItemButton';
import messages from './messages';
import { Link } from '@tanstack/react-router';

export const createApplicationInfoString = (application: {
  shiftStartDate?: string;
  shiftEndDate?: string;
}) => {
  const { shiftStartDate, shiftEndDate } = application;
  const dateString = moment(shiftStartDate).format('ddd L');
  const fromTime = moment(shiftStartDate).format('LT');
  const toTime = moment(shiftEndDate).format('LT');
  return `${dateString}, ${fromTime} - ${toTime}`;
};

type UpcomingAssignmentCard = {
  publisherId: number;
  upcomingAssignments: AssignmentSummaryResource[];
};

export const UpcomingAssignmentsCard = (props: UpcomingAssignmentCard) => {
  const intl = useIntl();
  const createAssignmentUrl = (assignment: {
    shiftStartDate?: string;
    shiftId?: number;
  }) => {
    const shiftDate = moment(assignment.shiftStartDate);
    return `/application/${shiftDate.isoWeekYear()}/${shiftDate.isoWeek()}/${shiftDate.weekday()}/${
      assignment.shiftId
    }`;
  };
  const { publisherId, upcomingAssignments } = props;
  return (
    <div data-test="upcoming-assignments-card">
      <ScrollablePaper
        title={<FormattedMessage {...messages.upcomingAssignments} />}
      >
        <List style={{ padding: 0 }}>
          {upcomingAssignments.length > 0 ? (
            upcomingAssignments.map((assignment) => {
              const isShiftLeader = publisherId === assignment.turnLeaderId;
              let secondaryText = assignment.routeName;
              let leftIcon = <PublisherIcon />;
              if (assignment.turnIsCanceled) {
                secondaryText = `${secondaryText} - ${intl.formatMessage(
                  messages.shiftCanceled
                )}`;
                leftIcon = <CanceledIcon color="error" />;
              } else if (isShiftLeader) {
                secondaryText = `${secondaryText} - ${intl.formatMessage(
                  messages.assignedAsShiftLeader
                )}`;
                leftIcon = <ShiftLeaderIcon />;
              }
              return (
                <ListItemButton
                  key={assignment.turnId}
                  component={Link}
                  to={createAssignmentUrl(assignment)}
                >
                  <ListItemIcon>{leftIcon}</ListItemIcon>
                  <ListItemText
                    primary={createApplicationInfoString(assignment)}
                    slots={{
                      secondary: 'div',
                    }}
                    secondary={
                      <TagList>
                        <span>{secondaryText}</span>
                        {assignment.hasVehicle && <TagVehicle />}
                      </TagList>
                    }
                  />
                </ListItemButton>
              );
            })
          ) : (
            <ListItem>
              <ListItemText
                primary={intl.formatMessage(messages.noUpcomingAssignments)}
              />
            </ListItem>
          )}
        </List>
      </ScrollablePaper>
    </div>
  );
};

export default UpcomingAssignmentsCard;
